import React, { useCallback, useContext, useEffect, useRef } from 'react';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import NavigationBar from '../components/NavigationBar';
import AccountPage from './AccountPage';
import EarningPage from './EarningPage';
import RewardsPage from './RewardsPage';
import {
  AuthenticatedLoyaltyRouteParams,
  authenticatedLoyaltyRoutePattern,
} from 'routes/merchant-scoped-portal/integrations/loyalty/routeUtils';

import PointExpirationPreview from '../components/PointsExpirationPreview';
import { ExpiringPointsDeadline } from '../models/ExpiringPointsDeadline';
import LoyaltyAccountStatus from '../components/LoyaltyAccountStatus';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { AppState } from 'store';
import {
  selectAccount,
  selectPassDownloadUrl,
  setTosState,
} from 'routes/merchant-scoped-portal/integrations/loyalty/loyaltySlice';
import {
  useGetTermsOfServiceQuery,
  useRetrieveAccountInfoQuery,
  useRetrievePersonalizedPassInfoQuery,
  useUpdateTermsOfServiceConsentMutation,
} from 'store/query/api-extensions/loyalty';
import ModuleLoadFailed from 'routes/profile/common/errors/ModuleLoadFailed';
import ModuleLoading from 'routes/profile/common/loading/ModuleLoading';
import { MerchantPortalContext } from 'routes/merchant-scoped-portal';
import { closeModal, ModalType, openModal } from 'store/modalSlice';
import { useTranslation } from 'react-i18next';
import { chooseConsentCopy } from 'routes/merchant-scoped-portal/integrations/loyalty/utils/consentCopy';
import { useTrackLoyaltyEvent } from 'utils/custom-react-hooks/loyalty/useTrackLoyaltyEvent';
import {
  ActionItem,
  EventName,
  FeatureFormat,
} from 'services/tracking/cdp/events/types';
import { logUnexpectedError } from 'routes/merchant-scoped-portal/utils/error';
import { Team } from 'routes/merchant-scoped-portal/teamRegistry';
import { isIphone } from 'utils/device';
import AddToAppleWalletSection from 'routes/merchant-scoped-portal/integrations/loyalty/components/AddToAppleWalletSection';
import LocalCashMigrationBanner from 'routes/merchant-scoped-portal/integrations/loyalty/components/LocalCashMigrationBanner';
import { MarketList } from '@market/react';
import { LoyaltyTermsOfService } from 'rpc/model/squareup/card/balance/model/loyalty-terms-of-service';

// Custom hook that fetches the loyalty terms of service for the loyalty account
// and shows the terms of service dialog if there are new terms to accept
const useShowTermsOfServiceModal = (
  loyaltyAccountLookupToken: string,
  loyaltyAccountToken: string,
  merchantId: string,
  isAccountReady: boolean
) => {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const store = useStore<AppState>();
  const dismissedConsent = useRef<{
    lookupToken: string;
    terms: LoyaltyTermsOfService;
  }>();

  const {
    currentData: tosState,
    isLoading,
    isFetching,
    isSuccess,
    error,
  } = useGetTermsOfServiceQuery(
    {
      loyaltyAccountLookupToken,
      merchantId,
    },
    { skip: !loyaltyAccountLookupToken }
  );

  const errorMessage = error?.message || error?.data?.message;
  if (errorMessage) {
    logUnexpectedError(
      new Error('Error fetching terms of service'),
      [Team.ConnectionsWeb],
      {
        error,
        errorMessage,
        loyaltyAccountToken,
        merchantId,
      }
    );
  }

  const availableNewTos = tosState?.availableNewTos;
  const latestAcceptedTos = tosState?.latestAcceptedTos;

  const [updateTermsOfService] = useUpdateTermsOfServiceConsentMutation();
  const trackLoyaltyEvent = useTrackLoyaltyEvent();
  const trackOpenLoyaltyTOSModal = useCallback(() => {
    trackLoyaltyEvent(EventName.VIEW_FEATURE, {
      event_description: 'View ToS consent modal',
      feature_format: FeatureFormat.MODAL,
    });
  }, [trackLoyaltyEvent]);

  useEffect(() => {
    if (
      isAccountReady &&
      isSuccess &&
      !error &&
      !isFetching &&
      availableNewTos &&
      !latestAcceptedTos &&
      loyaltyAccountLookupToken &&
      !(
        dismissedConsent.current?.lookupToken === loyaltyAccountLookupToken &&
        dismissedConsent.current?.terms.countryCode ===
          availableNewTos.countryCode &&
        dismissedConsent.current?.terms.version === availableNewTos.version
      )
    ) {
      const handleAgreeToTermsOfServiceConsent = async () => {
        dismissedConsent.current = {
          lookupToken: loyaltyAccountLookupToken,
          terms: availableNewTos,
        };
        try {
          trackLoyaltyEvent(EventName.CLICK_FEATURE, {
            action_item: ActionItem.CLICK_BUTTON,
            event_description: 'Accept ToS from consent modal',
            feature_format: FeatureFormat.BUTTON,
          });
          await updateTermsOfService({
            loyaltyAccountLookupToken,
            newlyAcceptedTos: availableNewTos,
          }).unwrap();
          dispatch(setTosState(undefined));
        } catch (error) {
          logUnexpectedError(
            new Error('Unable to agree to terms of service'),
            [Team.ConnectionsWeb],
            {
              availableNewTos,
              error,
              loyaltyAccountToken,
            }
          );
          // If the update fails for some reason, we will still dismiss the modal.
          // Since the consent hasn't been updated, the next visit to the page
          // will show the modal again.
          dispatch(setTosState(undefined));
        }
      };

      const handleDeclineToTermsOfServiceConsent = () => {
        history.push(`/merchantportal/${merchantId}/loyalty`);
      };

      const tosCopy = chooseConsentCopy(i18n.language, availableNewTos.copies);

      if (tosCopy) {
        dispatch(
          openModal({
            props: {
              header: t('loyalty.termsOfService.header'),
              onRender: trackOpenLoyaltyTOSModal,
              persistent: true,
              primaryButtonAction: handleAgreeToTermsOfServiceConsent,
              primaryButtonLabel: t('common.agree'),
              secondaryButtonAction: handleDeclineToTermsOfServiceConsent,
              secondaryButtonLabel: t('common.decline'),
              subtext: tosCopy.content,
            },
            type: ModalType.BasicDialog,
          })
        );
        return () => {
          const activeModal = store.getState().modal.activeModal;
          // A later dialog may have replaced this one before the account changed.
          if (
            activeModal?.type === ModalType.BasicDialog &&
            activeModal.props &&
            'primaryButtonAction' in activeModal.props &&
            activeModal.props.primaryButtonAction ===
              handleAgreeToTermsOfServiceConsent
          ) {
            dispatch(closeModal());
          }
        };
      } else {
        logUnexpectedError(
          new Error('Consent copy not found for available new TOS'),
          [Team.ConnectionsWeb],
          {
            availableNewTos,
            loyaltyAccountToken,
          }
        );
      }
    }
    return undefined;
  }, [
    availableNewTos,
    dispatch,
    error,
    history,
    i18n.language,
    isAccountReady,
    isFetching,
    isSuccess,
    loyaltyAccountLookupToken,
    loyaltyAccountToken,
    merchantId,
    t,
    latestAcceptedTos,
    store,
    trackLoyaltyEvent,
    trackOpenLoyaltyTOSModal,
    updateTermsOfService,
  ]);

  return {
    error,
    isLoading,
  };
};

const AuthenticatedLoyaltyResourcePage = () => {
  const match = useRouteMatch<Partial<AuthenticatedLoyaltyRouteParams>>(
    authenticatedLoyaltyRoutePattern
  );
  const location = useLocation();
  const loyaltyAccountLookupToken =
    match?.params?.loyaltyAccountLookupToken ?? '';
  const mpContext = useContext(MerchantPortalContext);
  const merchantId =
    mpContext.merchantPortalBaseData?.merchantOverview.merchantId ?? '';

  // Check for loyalty migration query parameter
  const queryParams = new URLSearchParams(location.search);
  const loyaltyMigrationDate = queryParams.get('loyalty_migration_date');

  const {
    currentData: accountInfo,
    isLoading: isLoadingAccountInfo,
    isFetching: isFetchingAccountInfo,
    isUninitialized,
    isError,
    isSuccess: isAccountSuccess,
  } = useRetrieveAccountInfoQuery(loyaltyAccountLookupToken, {
    skip: !loyaltyAccountLookupToken,
  });

  const currentAccount = accountInfo?.account;
  const account = useSelector((state: AppState) => {
    const storedAccount = selectAccount(state.loyaltyMerchantPortal);
    // Preserve local balance updates only for the account validated by this query.
    return isAccountSuccess &&
      currentAccount?.loyaltyAccount &&
      storedAccount?.loyaltyAccount?.id === currentAccount.loyaltyAccount.id
      ? storedAccount
      : currentAccount;
  });
  const isAccountReady = Boolean(
    isAccountSuccess &&
      !isError &&
      !isFetchingAccountInfo &&
      currentAccount?.loyaltyAccount
  );

  const loyaltyAccountToken = account?.loyaltyAccount?.id ?? '';

  const { isLoading: isLoadingTos } = useShowTermsOfServiceModal(
    loyaltyAccountLookupToken,
    loyaltyAccountToken,
    merchantId,
    isAccountReady
  );

  const { isLoading: isLoadingPassInfo } = useRetrievePersonalizedPassInfoQuery(
    loyaltyAccountToken,
    {
      skip: !(loyaltyAccountToken && isIphone()),
    }
  );

  const passDownloadUrl = useSelector((state: AppState) =>
    selectPassDownloadUrl(state.loyaltyMerchantPortal)
  );

  if (
    isLoadingAccountInfo ||
    (isFetchingAccountInfo && !account) ||
    isLoadingTos ||
    isUninitialized ||
    isLoadingPassInfo
  ) {
    return <ModuleLoading embedded={true} />;
  }

  if (isError || !account?.loyaltyAccount) {
    return <ModuleLoadFailed embedded={true} />;
  }

  const expiringPointDeadlines =
    account.loyaltyAccount.expiringPointDeadlines?.map((deadline) =>
      ExpiringPointsDeadline.fromProto(deadline)
    );

  return (
    <>
      <div className="mt-[-140px]">
        <LoyaltyAccountStatus
          loyaltyAccount={account.loyaltyAccount}
          member={account.member}
        />
      </div>
      <div className="grid gap-y-[16px]">
        {isIphone() && passDownloadUrl && (
          <AddToAppleWalletSection loyaltyPassDownloadUrl={passDownloadUrl} />
        )}
        {expiringPointDeadlines?.length ? (
          <MarketList>
            <PointExpirationPreview
              data-testid="point-expiration-preview"
              deadlines={expiringPointDeadlines}
            />
          </MarketList>
        ) : null}
        {loyaltyMigrationDate && (
          <LocalCashMigrationBanner migrationDate={loyaltyMigrationDate} />
        )}
        <NavigationBar />
        <Switch>
          <Route
            exact
            path={`${authenticatedLoyaltyRoutePattern}/account`}
            component={AccountPage}
          />
          <Route
            path={`${authenticatedLoyaltyRoutePattern}/earning`}
            component={EarningPage}
          />
          <Route
            exact
            path={`${authenticatedLoyaltyRoutePattern}/rewards`}
            component={RewardsPage}
          />
          <Redirect
            from={authenticatedLoyaltyRoutePattern}
            to={`${authenticatedLoyaltyRoutePattern}/rewards`}
          />
        </Switch>
      </div>
    </>
  );
};

export default AuthenticatedLoyaltyResourcePage;
